import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { UserProfile, UserRole } from '@/contexts/AuthContext';

export const useUsers = (role?: UserRole) => {
  return useQuery({
    queryKey: ['users', role],
    queryFn: async () => {
      try {
        // Check if user is authenticated before making the request
        const { data: { user } } = await supabase.auth.getUser();
        if (!user) {
          throw new Error('Usuario no autenticado');
        }

        let query = supabase
          .from('user_profiles')
          .select(`
            *,
            user_property_assignments (
              property_id,
              properties (
                id,
                title,
                short_name
              )
            )
          `);

        if (role) {
          query = query.eq('role', role);
        }

        const { data, error } = await query.order('created_at', { ascending: false });

        if (error) {
          console.error('Error fetching users:', error);
          throw error;
        }

        return data || [];
      } catch (error) {
        console.error('Error in useUsers query:', error);
        throw error;
      }
    },
    enabled: true, // Enable automatic execution since it's used in admin components
  });
};

export const useUserProperties = (userId: string) => {
  return useQuery({
    queryKey: ['user-properties', userId],
    queryFn: async () => {
      // Check if user is authenticated and userId is provided
      if (!userId) {
        throw new Error('ID de usuario requerido');
      }

      const { data: { user } } = await supabase.auth.getUser();
      if (!user) {
        throw new Error('Usuario no autenticado');
      }

      const { data, error } = await supabase
        .from('user_property_assignments')
        .select(`
          property_id,
          properties (
            id,
            title,
            short_name
          )
        `)
        .eq('user_id', userId);

      if (error) {
        console.error('Error fetching user properties:', error);
        throw error;
      }

      return data;
    },
    enabled: !!userId, // Only enable when userId is provided
  });
};
